// Shared per-coin flow-summary poller for PositionSummary widget.
// Body and header components both call useSharedFlowSummary(coin, window)
// — they hit the same module-level cache, so the 1h/24h toggle in the
// header instantly reflects in the body's bars without two fetches.

import { API_BASE_URL } from '../../../utils/apiConfig';
import { createSharedStore } from './createSharedStore';

const POLL_INTERVAL = 30_000;
const FETCH_TIMEOUT = 10_000;

const timers = new Map();
const inflight = new Map();

function parseKey(key) {
  const [coin, window] = key.split('|');
  return { coin, window };
}

async function fetchOnce(key, storeApi) {
  const { coin, window } = parseKey(key);
  if (!coin) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

  inflight.get(key)?.abort();
  const controller = new AbortController();
  inflight.set(key, controller);
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const url = `${API_BASE_URL}/api/coin/${encodeURIComponent(coin)}/flow-summary?window=${window}`;
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json && json.ok) {
      storeApi.set(key, { data: json, loading: false, error: null });
    } else {
      storeApi.set(key, { ...storeApi.get(key), loading: false, error: json?.error || 'failed' });
    }
  } catch (err) {
    clearTimeout(timeout);
    if (err?.name === 'AbortError') return;
    storeApi.set(key, { ...storeApi.get(key), loading: false, error: err.message });
  } finally {
    if (inflight.get(key) === controller) inflight.delete(key);
  }
}

export const sharedFlowSummary = createSharedStore({
  name: 'flowSummary',
  emptyValue: () => ({ data: null, loading: true, error: null }),
  startStream(key, storeApi) {
    fetchOnce(key, storeApi);
    const id = setInterval(() => fetchOnce(key, storeApi), POLL_INTERVAL);
    timers.set(key, id);
  },
  stopStream(key) {
    const id = timers.get(key);
    if (id) { clearInterval(id); timers.delete(key); }
    inflight.get(key)?.abort();
    inflight.delete(key);
  },
});

export function useSharedFlowSummary(coin, windowValue) {
  return sharedFlowSummary.useShared(coin && windowValue ? `${coin}|${windowValue}` : null);
}
